import type { Dispatch, SetStateAction } from "react";
import type { AutoFixState } from "@/hooks/chat/useAutoFixLoop";

export function AutoFixStatusBars({
  autoFixState,
  errorExpanded,
  setErrorExpanded,
  onCancelAutoFix,
  onResetAutoFix,
}: {
  autoFixState: AutoFixState;
  errorExpanded: boolean;
  setErrorExpanded: Dispatch<SetStateAction<boolean>>;
  onCancelAutoFix: () => void;
  onResetAutoFix: () => void;
}) {
  return (
    <>
      {autoFixState.status === "fixing" && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-amber-50 dark:bg-amber-950/30 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              Resolving compilation issues... ({autoFixState.attempt}/
              {autoFixState.maxAttempts})
            </span>
          </div>
          <button
            type="button"
            onClick={onCancelAutoFix}
            className="text-xs text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30"
          >
            Cancel
          </button>
        </div>
      )}
      {autoFixState.status === "success" && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-green-50 dark:bg-green-950/30 flex-shrink-0">
          <span className="text-xs font-medium text-green-700 dark:text-green-400">
            Fixed on attempt {autoFixState.attempt}
            {autoFixState.explanation && ` — ${autoFixState.explanation}`}
          </span>
          <button
            type="button"
            onClick={onResetAutoFix}
            className="text-xs text-green-700 dark:text-green-400 px-2 py-0.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30"
          >
            Dismiss
          </button>
        </div>
      )}
      {autoFixState.status === "failed" && (
        <div className="px-4 py-2 border-b bg-red-50 dark:bg-red-950/30 flex-shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-red-700 dark:text-red-400">
              Could not resolve after {autoFixState.maxAttempts} attempts.
            </span>
            <div className="flex items-center gap-1">
              {autoFixState.lastError && (
                <button
                  type="button"
                  onClick={() => setErrorExpanded((prev) => !prev)}
                  className="text-xs text-red-700 dark:text-red-400 px-2 py-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                >
                  {errorExpanded ? "Collapse" : "Details"}
                </button>
              )}
              <button
                type="button"
                onClick={onResetAutoFix}
                className="text-xs text-red-700 dark:text-red-400 px-2 py-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
              >
                Dismiss
              </button>
            </div>
          </div>
          {autoFixState.lastError && (
            <div
              className={`mt-1.5 ${errorExpanded ? "" : "max-h-12 overflow-hidden"}`}
            >
              <pre className="text-xs text-red-600 dark:text-red-500 whitespace-pre-wrap font-mono leading-relaxed break-words">
                {autoFixState.lastError}
              </pre>
            </div>
          )}
        </div>
      )}
    </>
  );
}
